import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom'; // Para pegar os dados do Workspace
import { useAuth } from '../../../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';

// menuItemStyle removido pois não estava sendo utilizado no novo layout de página

const Governanca = () => {
    // --- CONTEXTO E ESTADOS ORIGINAIS PRESERVADOS ---
    const { workspaceData } = useOutletContext();
    const { currentUser } = useAuth();
    
    // Estados para armazenar os dados que vinham via props na Modal
    const [data, setData] = useState({ filiais: [], equipe: [] });
    const [loadingData, setLoadingData] = useState(true);

    const { filiais, equipe } = data;
    
    // Identifica se o usuário logado é OWNER global da organização
    const isGlobalOwner = equipe.some(m => m.uid === currentUser?.uid && m.role === 'OWNER');

    // Estados de Controle
    const [view, setView] = useState('LIST'); 
    const [isProcessing, setIsProcessing] = useState(false);
    const [activeUnitCnpj, setActiveUnitCnpj] = useState("");

    // Estados de Formulário
    const [newUnitData, setNewUnitData] = useState({ cnpj: '', slug: '' });
    const [newUserData, setNewUserData] = useState({ email: '', role: 'USER', selectedWorkspaces: [] });

    // === ESTADOS DE PREÇO (CAIXA PRETA API) ===
    const [precosAtivos, setPrecosAtivos] = useState({ filial: '---', seat: '---' });

    // 1. Efeito de Carregamento dos Dados (Transposição da lógica do PortalShell para o Módulo)
    useEffect(() => {
        const carregarDadosGovernanca = async () => {
            if (!workspaceData?.cnpj_raiz) return;
            setLoadingData(true);
            try {
                const getAdminData = httpsCallable(functions, 'getAdminPanelData');
                const response = await getAdminData({ cnpjRaiz: workspaceData.cnpj_raiz });
                
                if (response.data.status === "SUCCESS") {
                    setData(response.data);
                    if (response.data.filiais.length > 0) {
                        setActiveUnitCnpj(response.data.filiais[0].cnpj_completo);
                    }
                }
            } catch (error) {
                console.error("Erro ao carregar dados de governança:", error);
            } finally {
                setLoadingData(false);
            }
        };
        carregarDadosGovernanca();
    }, [workspaceData?.cnpj_raiz]);

    useEffect(() => {
        const buscarPrecos = async () => {
            try {
                const getPrecos = httpsCallable(functions, 'getPrecosAtuais');
                const response = await getPrecos();
                setPrecosAtivos({
                    filial: response.data.valorFilial,
                    seat: response.data.valorSeat
                });
            } catch (error) {
                console.error("Falha silenciosa ao buscar tabela de preços", error);
            }
        };
        buscarPrecos();
    }, []);

    const [convitesPendentes, setConvitesPendentes] = useState([]);

    // --- CORREÇÃO ESLINT: Função memorizada para evitar loops e avisos de dependência ---
    const buscarConvites = useCallback(async () => {
        if (!isGlobalOwner || !data.equipe[0]?.cnpj_raiz) return;
        try {
            const getInvites = httpsCallable(functions, 'getPendingInvites');
            const response = await getInvites({ cnpjRaiz: data.equipe[0].cnpj_raiz });
            setConvitesPendentes(response.data.convites || []);
        } catch (e) { console.error(e); }
    }, [isGlobalOwner, data.equipe]);

    useEffect(() => {
        buscarConvites();
    }, [buscarConvites]);

    // FILTRO DINÂMICO
    const equipeAtiva = useMemo(() => {
        return equipe.filter(m => m.workspaceId === activeUnitCnpj);
    }, [equipe, activeUnitCnpj]);

    // --- MÁSCARA AUTOMÁTICA DE CNPJ ---
    const formatCNPJ = (value) => {
        const nums = value.replace(/\D/g, "");
        return nums
            .replace(/^(\d{2})(\d)/, "$1.$2")
            .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
            .replace(/\.(\d{3})(\d)/, ".$1/$2")
            .replace(/(\d{4})(\d)/, "$1-$2")
            .substring(0, 18);
    };

    const handleCNPJChange = (e) => {
        setNewUnitData({ ...newUnitData, cnpj: formatCNPJ(e.target.value) });
    };

    // --- FUNÇÕES DE CONEXÃO PRESERVADAS 100% ---
    const handleAddUnit = async () => {
        setIsProcessing(true);
        try {
            const addFilialFn = httpsCallable(functions, 'addFilialWorkspace');
            const response = await addFilialFn({
                cnpjNovaFilial: newUnitData.cnpj,
                customSlug: newUnitData.slug,
                cnpjRaizOwner: data.equipe.find(m => m.uid === currentUser?.uid)?.cnpj_raiz
            });

            if (response.data.status === "SUCCESS") {
                alert(`✅ Nova Filial provisionada! Assinatura atualizada no Asaas.`);
                window.location.reload(); 
            }
        } catch (error) {
            alert(`Falha ao provisionar filial: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleAddUser = async () => {
        setIsProcessing(true);
        try {
            const inviteUserFn = httpsCallable(functions, 'inviteUserWorkspace');
            const response = await inviteUserFn({
                emailConvidado: newUserData.email,
                roleConvidado: newUserData.role,
                workspacesAlvo: newUserData.selectedWorkspaces,
                cnpjRaizOwner: data.equipe.find(m => m.uid === currentUser?.uid)?.cnpj_raiz
            });

            if (response.data.status === "SUCCESS") {
                alert(`✅ Convite enviado com sucesso!`);
                await buscarConvites();
                setView('LIST');
                setNewUserData({ email: '', role: 'USER', selectedWorkspaces: [] });
            }
        } catch (error) {
            alert(`Falha ao enviar convite: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteUser = async (targetUser) => {
        if (targetUser.role === 'OWNER') {
            return alert("Ação bloqueada. Impossível revogar acesso de um Sócio-Proprietário.");
        }
        
        if (window.confirm(`Tem certeza que deseja revogar o acesso de ${targetUser.email}?`)) {
            setIsProcessing(true);
            try {
                const revokeUserFn = httpsCallable(functions, 'revokeUserAccess');
                const response = await revokeUserFn({
                    targetUid: targetUser.uid,
                    targetWorkspaceId: targetUser.workspaceId,
                    cnpjRaizOwner: targetUser.cnpj_raiz
                });

                if (response.data.status === "SUCCESS") {
                    alert("Acesso revogado com sucesso.");
                    window.location.reload();
                }
            } catch (error) {
                alert("Falha na operação: " + error.message);
            } finally {
                setIsProcessing(false);
            }
        }
    };

    const toggleWorkspaceSelection = (cnpj) => {
        setNewUserData(prev => {
            const isSelected = prev.selectedWorkspaces.includes(cnpj);
            if (isSelected) {
                return { ...prev, selectedWorkspaces: prev.selectedWorkspaces.filter(c => c !== cnpj) };
            } else {
                return { ...prev, selectedWorkspaces: [...prev.selectedWorkspaces, cnpj] };
            }
        });
    };

    if (loadingData) return (
        <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#fff' }}>
            <div className="pts-spinner"></div>
            <div className="pts-loader-text">Carregando Painel de Governança...</div>
        </div>
    );

    return (
        <div className="pts-governanca-module" style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', background: '#fff' }}>
            
            <div style={{ padding: '30px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {view !== 'LIST' && (
                        <button onClick={() => setView('LIST')} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>←</button>
                    )}
                    <div>
                        <h2 style={{ color: '#0f172a', margin: '0 0 5px 0', fontSize: '1.8rem', fontWeight: '800' }}>
                            {view === 'LIST' ? 'Painel de Governança' : view === 'ADD_UNIT' ? 'Nova Unidade de Negócio' : 'Convidar Colaborador'}
                        </h2>
                        <p style={{ color: '#64748b', margin: 0 }}>
                            {view === 'LIST' ? 'Gerencie as filiais e a distribuição da sua equipe.' : 'Configuração de faturamento e expansão operacional.'}
                        </p>
                    </div>
                </div>
            </div>

            <div style={{ flexGrow: 1, overflowY: 'auto', padding: '30px' }}>
                
                {view === 'LIST' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '40px' }}>
                        
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '1.1rem', color: '#0369a1', margin: 0, fontWeight: '700' }}>🏢 Unidades</h3>
                                {isGlobalOwner && (
                                    <button className="pts-btn-outline" onClick={() => setView('ADD_UNIT')} style={{ padding: '6px 12px', fontSize: '0.75rem' }}>+ Add Filial</button>
                                )}
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {filiais.map(filial => {
                                    const isActive = activeUnitCnpj === filial.cnpj_completo;
                                    return (
                                        <div 
                                            key={filial.cnpj_completo} 
                                            onClick={() => setActiveUnitCnpj(filial.cnpj_completo)}
                                            style={{ 
                                                background: isActive ? '#f0f9ff' : '#fff', 
                                                padding: '20px', 
                                                borderRadius: '16px', 
                                                border: `2px solid ${isActive ? '#0ea5e9' : '#e2e8f0'}`, 
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                boxShadow: isActive ? '0 4px 12px rgba(14, 165, 233, 0.1)' : 'none'
                                            }}
                                        >
                                            <strong style={{ display: 'block', fontSize: '1rem', color: isActive ? '#0369a1' : '#0f172a' }}>
                                                {filial.nome_exibicao}
                                            </strong>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', alignItems: 'center' }}>
                                                <small style={{ color: '#64748b', fontFamily: 'monospace' }}>{filial.cnpj_completo}</small>
                                                <span style={{ fontSize: '0.65rem', fontWeight: '800', background: filial.tipo_unidade === 'MATRIZ' ? '#e0f2fe' : '#f1f5f9', color: filial.tipo_unidade === 'MATRIZ' ? '#0369a1' : '#64748b', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
                                                    {filial.tipo_unidade}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', background: '#f8fafc', padding: '15px 20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <h3 style={{ fontSize: '1.1rem', color: '#10b981', margin: 0, fontWeight: '700' }}>👥 Equipe Ativa</h3>
                                <button 
                                    className="pts-btn-outline" 
                                    onClick={() => {
                                        setNewUserData(prev => ({ ...prev, selectedWorkspaces: isGlobalOwner ? [] : [activeUnitCnpj] }));
                                        setView('ADD_USER');
                                    }} 
                                    style={{ padding: '8px 16px', fontSize: '0.8rem', borderColor: '#10b981', color: '#10b981', fontWeight: '700' }}
                                >
                                    + Convidar Colaborador
                                </button>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                                {equipeAtiva.length === 0 ? (
                                    <p style={{ gridColumn: '1/-1', textAlign: 'center', color: '#94a3b8', padding: '40px' }}>Nenhum colaborador nesta unidade.</p>
                                ) : (
                                    equipeAtiva.map(membro => (
                                        <div key={membro.uid} style={{ background: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'border 0.2s', gap: '15px' }}>
                                            
                                            {/* ESQUERDA: Adicionado flex: 1 e minWidth: 0 para o texto respeitar a caixa */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <strong style={{ display: 'block', fontSize: '1rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {membro.nome}
                                                </strong>
                                                <small style={{ color: '#64748b', fontSize: '0.85rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {membro.email}
                                                </small>
                                            </div>
                                            
                                            {/* DIREITA: flexShrink: 0 garante que os botões nunca sejam esmagados */}
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: '900', color: membro.role === 'OWNER' ? '#0369a1' : '#10b981', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    {membro.role}
                                                </span>
                                                {membro.role !== 'OWNER' && (
                                                    <span onClick={() => handleDeleteUser(membro)} style={{ fontSize: '0.75rem', color: '#ef4444', cursor: 'pointer', fontWeight: '700', textDecoration: 'underline' }}>Revogar Acesso</span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {convitesPendentes.length > 0 && (
                                <div style={{ marginTop: '40px', borderTop: '2px dashed #e2e8f0', paddingTop: '25px' }}>
                                    <h4 style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '15px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase' }}>📩 Convites aguardando aceite</h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {convitesPendentes.map(c => (
                                            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 20px', background: '#f8fafc', borderRadius: '10px', fontSize: '0.9rem', border: '1px solid #e2e8f0' }}>
                                                <span style={{ color: '#475569', fontWeight: '500' }}>{c.email}</span>
                                                <span style={{ fontWeight: '800', color: '#0ea5e9' }}>{c.role}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {view === 'ADD_UNIT' && (
                    <div className="pts-form-step" style={{ maxWidth: '500px', margin: '0 auto' }}>
                        <div style={{ background: '#e0f2fe', border: '1px solid #bae6fd', padding: '20px', borderRadius: '12px', marginBottom: '30px' }}>
                            <strong style={{ color: '#0369a1', fontSize: '1rem' }}>ℹ️ Expansão de Infraestrutura</strong>
                            <p style={{ color: '#0c4a6e', fontSize: '0.9rem', margin: '10px 0 0 0', lineHeight: '1.4' }}>
                                A inclusão de uma nova filial adicionará <strong>R$ {precosAtivos.filial !== '---' ? precosAtivos.filial.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '---'}/mês</strong> à fatura corporativa.
                            </p>
                        </div>
                        
                        <label className="pts-label">CNPJ da Nova Filial</label>
                        <input className="pts-dynamic-input" placeholder="00.000.000/0000-00" value={newUnitData.cnpj} onChange={handleCNPJChange} />
                        
                        <label className="pts-label">URL de Acesso Exclusiva</label>
                        <div className="pts-vanity-url">
                            <span>pts.dev.br/</span>
                            <input placeholder="ex: lab-curitiba" value={newUnitData.slug} onChange={e => setNewUnitData({...newUnitData, slug: e.target.value})} />
                        </div>

                        <button className="pts-btn-launch" disabled={isProcessing || newUnitData.cnpj.length < 18} onClick={handleAddUnit}>
                            {isProcessing ? 'VALIDANDO NA RECEITA FEDERAL...' : 'CONFIRMAR ASSINATURA E ADICIONAR'}
                        </button>
                    </div>
                )}

                {view === 'ADD_USER' && (
                    <div className="pts-form-step" style={{ maxWidth: '550px', margin: '0 auto' }}>
                        <div style={{ background: '#dcfce7', border: '1px solid #86efac', padding: '20px', borderRadius: '12px', marginBottom: '30px' }}>
                            <strong style={{ color: '#16a34a', fontSize: '1rem' }}>ℹ️ Licenciamento Per-Seat</strong>
                            <p style={{ color: '#14532d', fontSize: '0.9rem', margin: '10px 0 0 0', lineHeight: '1.4' }}>
                                Cada novo acesso adiciona <strong>R$ {precosAtivos.seat !== '---' ? precosAtivos.seat.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '---'}/mês</strong> à fatura.
                            </p>
                        </div>

                        <label className="pts-label">E-mail Corporativo do Colaborador</label>
                        <input type="email" className="pts-dynamic-input" placeholder="engenheiro@suaempresa.com.br" value={newUserData.email} onChange={e => setNewUserData({...newUserData, email: e.target.value})} />
                        
                        <label className="pts-label">Nível de Permissão (Role)</label>
                        <select className="pts-dynamic-input" value={newUserData.role} onChange={e => setNewUserData({...newUserData, role: e.target.value})}>
                            <option value="MANAGER">Gestor Local (Pode aprovar ensaios e ver equipe local)</option>
                            <option value="USER">Técnico/Laboratorista (Apenas inserir dados e ensaios)</option>
                        </select>

                        <label className="pts-label" style={{ marginTop: '10px' }}>Designar para quais unidades?</label>
                        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {filiais.map(f => {
                                const isDisabled = !isGlobalOwner && f.cnpj_completo !== activeUnitCnpj;
                                const isChecked = newUserData.selectedWorkspaces.includes(f.cnpj_completo);
                                return (
                                    <label key={f.cnpj_completo} style={{ display: 'flex', alignItems: 'center', gap: '12px', opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                                        <input type="checkbox" checked={isChecked} disabled={isDisabled} onChange={() => toggleWorkspaceSelection(f.cnpj_completo)} style={{ width: '18px', height: '18px' }} />
                                        <span style={{ fontSize: '1rem', color: '#0f172a', fontWeight: '500' }}>
                                            {f.nome_exibicao} <small style={{ color: '#64748b' }}>({f.tipo_unidade})</small>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>

                        <button className="pts-btn-launch" disabled={isProcessing || !newUserData.email || newUserData.selectedWorkspaces.length === 0} onClick={handleAddUser}>
                            {isProcessing ? 'GERANDO TOKEN SEGURO...' : 'ADICIONAR LICENÇA E ENVIAR CONVITE'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Governanca;